import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-background-200/70">
      <nav className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
              Mulebuy<span className="text-primary-500">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer font-medium">
              <i className="ri-home-4-line text-sm"></i>
              Home
            </Link>

            <Link to="/shopping-guide" className="flex items-center gap-1 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer font-medium">
              <i className="ri-shopping-bag-3-line text-sm"></i>
              Shopping Guide
            </Link>

            <Link to="/what-is-mulebuy-spreadsheet" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              What Is It
            </Link>
            <Link to="/best-mulebuy-spreadsheets" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Best Lists
            </Link>
            <Link to="/mulebuy-agents-comparison" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Compare Agents
            </Link>
            <Link to="/is-mulebuy-spreadsheet-safe" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
              Safety Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-white text-sm font-medium cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                Categories
                <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-800"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/70">
            <div className="flex flex-col gap-1 pt-3">
              <Link to="/" className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer font-medium" onClick={() => setIsMobileMenuOpen(false)}>
                <i className="ri-home-4-line"></i> Home
              </Link>
              <Link to="/shopping-guide" className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer font-medium" onClick={() => setIsMobileMenuOpen(false)}>
                <i className="ri-shopping-bag-3-line"></i> Shopping Guide
              </Link>

              <div className="px-3 py-2 text-xs text-foreground-500 font-semibold uppercase mt-2">Guides</div>
              <Link to="/what-is-mulebuy-spreadsheet" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                What Is Mulebuy Spreadsheet
              </Link>
              <Link to="/best-mulebuy-spreadsheets" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                Best Spreadsheet Lists
              </Link>
              <Link to="/mulebuy-agents-comparison" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                Compare Agents
              </Link>
              <Link to="/is-mulebuy-spreadsheet-safe" className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
                Safety Guide
              </Link>

              <div className="px-3 py-2 text-xs text-foreground-500 font-semibold uppercase mt-2">Product Categories</div>
              <div className="grid grid-cols-2 gap-1 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-1.5 px-2 py-2 text-xs text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer whitespace-nowrap"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="mx-3 mt-3 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold text-center cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}